import type { Block, InputMode } from '../types'
import { grade as gradeChinese } from './grade'
import { gradeText } from './gradeText'
import type { GradeResult } from './grade'

/**
 * What a language needs in order to be drillable here.
 *
 * The app began as a Mandarin trainer and its vocabulary shows: a block stores
 * `hanzi` and `pinyin`. Those field names are now generic in meaning — `hanzi`
 * is the target text in whatever language, `pinyin` is the phonetic line for
 * languages that have one. They were kept rather than renamed because every
 * record already synced to the server carries those keys, and renaming would
 * mean rewriting stored JSON payloads for no behavioural gain. Use the helpers
 * below rather than reading the fields directly.
 */

export type LanguageCode = 'zh' | 'de' | 'fr' | 'es' | 'it'

export interface LanguageDef {
  code: LanguageCode
  /** English name, for menus. */
  name: string
  /** The language's own name, shown alongside. */
  nativeName: string
  /** BCP-47 tag for speech synthesis. */
  speech: string
  /** Whether the phonetic line exists and is worth drilling separately. */
  hasPhonetics: boolean
  /** Input modes offered in the typing drills. */
  modes: InputMode[]
  /** Label for the primary text field, in the editor and drills. */
  targetLabel: string
  /** Label for the phonetic field, or null when there isn't one. */
  phoneticLabel: string | null
  /**
   * What a near-miss is called. Mandarin's is a tone; German's is a form —
   * right word, wrong capitalisation or umlaut. Same machinery, different name.
   */
  nearMissLabel: string
  /** Placeholder for the answer box. */
  answerPlaceholder: string
  /** Hint under the input, explaining what the grader accepts. */
  inputHint: string
  /**
   * The decorative native words in the interface itself.
   *
   * The app was built for Mandarin and its chrome was Chinese throughout —
   * 今天, 情景, 突袭 — which is charming on the Mandarin site and nonsense on
   * the German one. Each language now carries its own.
   */
  ui: {
    brand: string
    today: string
    todayTitle: string
    islands: string
    scenarios: string
    ambush: string
    settings: string
    accounts: string
    /** The rail's Help section, in the language's own words. */
    help: string
    /** The rail's Grammar section. */
    grammar: string
    signIn: string
    listen: string
    recall: string
    perform: string
    result: string
    gotOut: string
    newIsland: string
  }
}

export const LANGUAGES: Record<LanguageCode, LanguageDef> = {
  zh: {
    code: 'zh',
    name: 'Mandarin Chinese',
    nativeName: '中文',
    speech: 'zh-CN',
    hasPhonetics: true,
    modes: ['hanzi', 'pinyin'],
    targetLabel: 'Characters',
    phoneticLabel: 'Pinyin',
    nearMissLabel: 'tone',
    answerPlaceholder: '在这里输入…',
    inputHint: 'Tone numbers or marks — wo3 hao3 or wǒ hǎo',
    ui: {
      brand: '语言岛',
      today: '今天',
      todayTitle: '今天练什么',
      islands: '语言岛',
      scenarios: '情景',
      ambush: '突袭',
      settings: '设置',
      accounts: '用户',
      help: '说明',
      grammar: '语法',
      signIn: '登录',
      listen: '跟读',
      recall: '回想',
      perform: '上台',
      result: '结果',
      gotOut: '走出来了',
      newIsland: '新岛',
    },
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    speech: 'de-DE',
    hasPhonetics: false,
    modes: ['text'],
    targetLabel: 'German',
    phoneticLabel: null,
    nearMissLabel: 'form',
    answerPlaceholder: 'hier tippen…',
    inputHint: 'ue, oe, ae and ss are accepted for ü, ö, ä and ß',
    ui: {
      brand: 'Sprachinsel',
      today: 'Heute',
      todayTitle: 'Was heute dran ist',
      islands: 'Inseln',
      scenarios: 'Szenen',
      ambush: 'Überfall',
      settings: 'Einstellungen',
      accounts: 'Konten',
      help: 'Hilfe',
      grammar: 'Grammatik',
      signIn: 'Anmelden',
      listen: 'Nachsprechen',
      recall: 'Abrufen',
      perform: 'Auftritt',
      result: 'Ergebnis',
      gotOut: 'Geschafft',
      newIsland: 'Neue Insel',
    },
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    speech: 'fr-FR',
    hasPhonetics: false,
    modes: ['text'],
    targetLabel: 'French',
    phoneticLabel: null,
    nearMissLabel: 'form',
    answerPlaceholder: 'tapez ici…',
    inputHint: 'Accents are scored but a missing one only costs a quarter mark',
    ui: {
      brand: 'Île aux langues',
      today: "Aujourd'hui",
      todayTitle: 'Au programme du jour',
      islands: 'Îles',
      scenarios: 'Scènes',
      ambush: 'Embuscade',
      settings: 'Réglages',
      accounts: 'Comptes',
      help: 'Aide',
      grammar: 'Grammaire',
      signIn: 'Connexion',
      listen: 'Répéter',
      recall: 'Rappel',
      perform: 'En scène',
      result: 'Résultat',
      gotOut: 'Vous voilà sorti',
      newIsland: 'Nouvelle île',
    },
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    speech: 'es-ES',
    hasPhonetics: false,
    modes: ['text'],
    targetLabel: 'Spanish',
    phoneticLabel: null,
    nearMissLabel: 'form',
    answerPlaceholder: 'escribe aquí…',
    inputHint: 'Accents and ñ are scored but a missing one only costs a quarter mark',
    ui: {
      brand: 'Isla de idiomas',
      today: 'Hoy',
      todayTitle: 'Qué toca hoy',
      islands: 'Islas',
      scenarios: 'Escenas',
      ambush: 'Emboscada',
      settings: 'Ajustes',
      accounts: 'Cuentas',
      help: 'Ayuda',
      grammar: 'Gramática',
      signIn: 'Entrar',
      listen: 'Repetir',
      recall: 'Recordar',
      perform: 'En escena',
      result: 'Resultado',
      gotOut: 'Saliste bien',
      newIsland: 'Isla nueva',
    },
  },
  it: {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    speech: 'it-IT',
    hasPhonetics: false,
    modes: ['text'],
    targetLabel: 'Italian',
    phoneticLabel: null,
    nearMissLabel: 'form',
    answerPlaceholder: 'scrivi qui…',
    inputHint: 'Accents are scored but a missing one only costs a quarter mark',
    ui: {
      brand: 'Isola delle lingue',
      today: 'Oggi',
      todayTitle: 'Cosa tocca oggi',
      islands: 'Isole',
      scenarios: 'Scene',
      ambush: 'Imboscata',
      settings: 'Impostazioni',
      accounts: 'Account',
      help: 'Guida',
      grammar: 'Grammatica',
      signIn: 'Accedi',
      listen: 'Ripetere',
      recall: 'Richiamo',
      perform: 'In scena',
      result: 'Risultato',
      gotOut: "Ce l'hai fatta",
      newIsland: 'Isola nuova',
    },
  },
}

export const LANGUAGE_LIST: LanguageDef[] = [
  LANGUAGES.zh,
  LANGUAGES.de,
  LANGUAGES.fr,
  LANGUAGES.es,
  LANGUAGES.it,
]

export const DEFAULT_LANGUAGE: LanguageCode = 'zh'

export function languageOf(code: string | undefined): LanguageDef {
  return LANGUAGES[(code as LanguageCode) ?? DEFAULT_LANGUAGE] ?? LANGUAGES[DEFAULT_LANGUAGE]
}

/** The sentence to produce, in the target language. */
export function targetText(block: Block): string {
  return block.hanzi
}

/** The phonetic line, empty for languages without one. */
export function phoneticText(block: Block): string {
  return block.pinyin
}

/**
 * Grade an attempt using the right engine for the language.
 *
 * Mandarin scores characters, or syllables and tones separately. Everything
 * else is graded word by word, which is the unit a learner of an alphabetic
 * language actually thinks in — a single wrong letter should not read as a
 * whole sentence gone wrong.
 */
export function gradeFor(
  language: LanguageCode,
  block: Block,
  attempt: string,
  mode: InputMode,
  requireTones: boolean,
): GradeResult {
  if (language === 'zh') {
    return gradeChinese(
      { hanzi: block.hanzi, pinyin: block.pinyin },
      attempt,
      mode === 'pinyin' ? 'pinyin' : 'hanzi',
      requireTones,
    )
  }
  return gradeText(targetText(block), attempt)
}

/** The mode to drill in, given the language and what the user last chose. */
export function resolveMode(language: LanguageCode, preferred: InputMode): InputMode {
  const modes = LANGUAGES[language].modes
  return modes.includes(preferred) ? preferred : modes[0]
}

/** Which typeface the answer box should use for a given mode. */
export function inputClass(mode: InputMode): string {
  if (mode === 'hanzi') return 'is-han'
  if (mode === 'pinyin') return 'is-data'
  return ''
}

/** Whether the tone ribbon and tone scoring mean anything here. */
export function hasTones(language: string): boolean {
  return language === 'zh'
}

/**
 * The subdomain each language lives on: mandarin.language-island.com,
 * german.language-island.com, and so on.
 *
 * Deliberately the English name rather than the ISO code — german. reads to
 * anyone, de. reads to nobody.
 */
export const LANGUAGE_HOST: Record<LanguageCode, string> = {
  zh: 'mandarin',
  de: 'german',
  fr: 'french',
  es: 'spanish',
  it: 'italian',
}

const HOST_LANGUAGE: Record<string, LanguageCode> = Object.fromEntries(
  Object.entries(LANGUAGE_HOST).map(([code, host]) => [host, code as LanguageCode]),
) as Record<string, LanguageCode>

/**
 * Which language this hostname serves, or null on the apex, a preview URL, or
 * localhost — where the in-app switcher stays in charge instead.
 */
/**
 * `globalThis.location` rather than `window.location` so this module stays free
 * of DOM types: the server validates generated content with the same grading
 * rules the browser uses, and it cannot import a file that mentions `window`.
 */
export function languageFromHost(
  hostname = (globalThis as { location?: { hostname?: string } }).location?.hostname ?? '',
): LanguageCode | null {
  const label = hostname.split('.')[0]?.toLowerCase()
  return label && HOST_LANGUAGE[label] ? HOST_LANGUAGE[label] : null
}

/** Absolute URL of a language's own site, preserving the current path. */
export function urlForLanguage(code: LanguageCode, hostname: string, path = '/'): string {
  const parts = hostname.split('.')
  // Drop an existing language label so switching hops sideways rather than
  // stacking up as german.mandarin.language-island.com.
  const base = languageFromHost(hostname) ? parts.slice(1).join('.') : hostname
  return `https://${LANGUAGE_HOST[code]}.${base}${path}`
}
